import { NavLink } from 'react-router-dom';
import { useTasks } from '@/api/hooks';
import { Icon, type IconName } from '../icon/Icon';
import styles from './Sidebar.module.css';

interface NavItem {
  to: string;
  icon: IconName;
  label: string;
  badge?: number;
}

const PROJECT_NAV: NavItem[] = [
  { to: '/', icon: 'home', label: 'Dashboard' },
  { to: '/tijdlijn', icon: 'timeline', label: 'Tijdlijn' },
  { to: '/documenten', icon: 'folder', label: 'Documenten' },
  { to: '/contacten', icon: 'users', label: 'Contacten' },
];

function Item({ to, icon, label, badge }: NavItem) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => `${styles.item} ${isActive ? styles.active : ''}`}
    >
      <span className={styles.iconWrap}>
        <Icon name={icon} size={15} />
      </span>
      <span>{label}</span>
      {badge !== undefined && badge > 0 && <span className={styles.badge}>{badge}</span>}
    </NavLink>
  );
}

// 2026-05-17 — Sidebar Taken badge reflects the real open-task count from
// the API. Previously it was hard-coded to {Taken: 5}. The badge hides when
// there are zero open tasks so an empty list doesn't shout for attention.
export function Sidebar() {
  // `total` from the server-side pagination envelope is authoritative.
  const openTasks = useTasks({ status: 'Open', pageSize: 1 });

  // PagedResult.total is typed as `number | string` by the OpenAPI codegen
  // (server emits an int but EF count can serialise as either). Coerce.
  const toNum = (v: number | string | undefined): number | undefined =>
    typeof v === 'number' ? v : typeof v === 'string' ? Number(v) || 0 : undefined;
  const tasksBadge = toNum(openTasks.data?.total);

  const werkNav: NavItem[] = [
    { to: '/budget', icon: 'euro', label: 'Budget' },
    { to: '/taken', icon: 'checkbox', label: 'Taken', badge: tasksBadge },
    { to: '/beslissingen', icon: 'decision', label: 'Beslissingen' },
    { to: '/fotos', icon: 'camera', label: 'Foto-dagboek' },
    { to: '/werfverslagen', icon: 'clipboard', label: 'Werfverslagen' },
    { to: '/keuringen', icon: 'shield', label: 'Keuringen' },
    { to: '/plannen', icon: 'arch', label: 'Plannen' },
  ];

  return (
    <nav className={styles.sidebar} aria-label="Hoofdnavigatie">
      <div className={styles.section}>Project</div>
      {PROJECT_NAV.map((item) => (
        <Item key={item.to} {...item} />
      ))}
      <div className={styles.section}>Werk</div>
      {werkNav.map((item) => (
        <Item key={item.to} {...item} />
      ))}
      <div className={styles.spacer} />
      <hr className={styles.hr} />
      <Item to="/instellingen" icon="settings" label="Instellingen" />
    </nav>
  );
}
